/**
 * Anticipatory loader — a wrapped gift that gently rattles + bobs (like a
 * present being shaken), themed with the actual wrap + ribbon colours so it
 * flows straight into whatever follows. Used during the unlock call (cold
 * start), tear-engine init, the create "wrapping" overlay, and auth rehydration.
 *
 * Pure CSS animation (no framer-motion) so it's light enough to live in the
 * eager bundle. prefers-reduced-motion is honoured globally (globals.css):
 * the animations freeze to a calm, static gift.
 */
import { wrapPatternStyle } from '@/lib/wrapPatterns';

interface GiftLoaderProps {
  themeHex?: string;
  ribbonHex?: string;
  wrapPatternId?: string;
  caption?: string;
}

export function GiftLoader({
  themeHex = '#FF6B6B',
  ribbonHex = '#FBBF24',
  wrapPatternId,
  caption = 'Getting your gift ready…',
}: GiftLoaderProps) {
  return (
    <div className="flex flex-col items-center gap-6">
      <div
        className="wrappa-gift-rattle relative h-28 w-28 rounded-2xl shadow-lg [filter:url(#painterly)]"
        style={wrapPatternStyle(themeHex, wrapPatternId)}
      >
        <div
          className="absolute inset-y-0 left-1/2 w-5 -translate-x-1/2"
          style={{ backgroundColor: ribbonHex, boxShadow: '0 0 0 1px rgb(28 25 23 / 0.12)' }}
        />
        <div
          className="absolute inset-x-0 top-1/2 h-5 -translate-y-1/2"
          style={{ backgroundColor: ribbonHex, boxShadow: '0 0 0 1px rgb(28 25 23 / 0.12)' }}
        />
      </div>

      <div className="flex flex-col items-center gap-2.5">
        <p className="text-sm text-ink-muted">{caption}</p>
        <div className="flex gap-1.5" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="wrappa-loader-dot h-1.5 w-1.5 rounded-full bg-ink-faint"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
